import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  MessageSquare, Check, X, Loader2, AlertTriangle,
  Send, RefreshCw, Trash2, Info, ExternalLink, Smartphone, ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Painel WhatsApp — integração oficial com WhatsApp Cloud API da Meta.
 * Fluxo "colar Access Token" (válido enquanto Embedded Signup não é aprovado).
 */
export const WhatsAppConfig = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [aiToggling, setAiToggling] = useState(false);

  const [form, setForm] = useState({
    access_token: '',
    phone_number_id: '',
    waba_id: '',
    business_id: '',
    whatsapp_number: '',
  });

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/whatsapp/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (e) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const submitRegister = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/whatsapp/register`, form, { withCredentials: true });
      toast.success(res.data.message || 'WhatsApp registrado!');
      if (res.data.connection?.success) toast.success(res.data.connection.message);
      else if (res.data.connection?.message) toast.error(res.data.connection.message);
      setShowRegister(false);
      setForm({ access_token: '', phone_number_id: '', waba_id: '', business_id: '', whatsapp_number: '' });
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar credenciais');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API_URL}/whatsapp/test-connection`, {}, { withCredentials: true });
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
      fetchStatus();
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Desconectar o WhatsApp desta loja?')) return;
    setDisconnecting(true);
    try {
      await axios.delete(`${API_URL}/whatsapp/disconnect`, { withCredentials: true });
      toast.success('WhatsApp desconectado');
      fetchStatus();
    } finally {
      setDisconnecting(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone) return toast.error('Digite um número para teste');
    setSendingTest(true);
    try {
      const res = await axios.post(`${API_URL}/whatsapp/send-test`, { to_number: testPhone }, { withCredentials: true });
      if (res.data.success) toast.success('Mensagem de teste enviada!');
      else toast.error(res.data.message || 'Falhou');
    } finally {
      setSendingTest(false);
    }
  };

  const toggleAI = async () => {
    if (!status?.configured) return;
    const next = !status.ai_enabled;
    setAiToggling(true);
    setStatus({ ...status, ai_enabled: next });
    try {
      const res = await axios.post(
        `${API_URL}/whatsapp/ai-toggle`,
        { enabled: next },
        { withCredentials: true }
      );
      toast.success(res.data.message || (next ? 'IA ativada' : 'IA desativada'));
    } catch (e) {
      setStatus({ ...status, ai_enabled: !next });
      toast.error(e.response?.data?.detail || 'Erro ao alterar IA');
    } finally {
      setAiToggling(false);
    }
  };


  // ─── Badges ──────────────────────────────────────────────
  const statusBadge = () => {
    const s = status?.status || 'disconnected';
    const map = {
      connected: { emoji: '🟢', label: 'Conectado', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
      pending_test: { emoji: '🟡', label: 'Aguardando teste', color: 'bg-amber-100 text-amber-800 border-amber-300' },
      error: { emoji: '🔴', label: 'Erro', color: 'bg-red-100 text-red-800 border-red-300' },
      disconnected: { emoji: '🔴', label: 'Desconectado', color: 'bg-slate-100 text-slate-700 border-slate-300' },
    };
    const c = map[s] || map.disconnected;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${c.color}`} data-testid="whatsapp-status-badge">
        {c.emoji} {c.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">WhatsApp</h1>
            <p className="text-sm text-slate-500">Atendimento automático via WhatsApp Cloud API oficial (Meta)</p>
          </div>
          {statusBadge()}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
          </div>
        ) : status?.configured ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="whatsapp-connected-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-slate-500">Número conectado</p>
                <p className="font-semibold text-slate-900" data-testid="whatsapp-number">
                  {status.display_phone_number || status.whatsapp_number || '—'}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Nome verificado</p>
                <p className="font-semibold text-slate-900">{status.verified_name || '—'}</p>
              </div>
              <div>
                <p className="text-slate-500">Phone Number ID</p>
                <p className="font-mono text-xs text-slate-800">{status.phone_number_id}</p>
              </div>
              <div>
                <p className="text-slate-500">WABA ID</p>
                <p className="font-mono text-xs text-slate-800">{status.waba_id}</p>
              </div>
              <div>
                <p className="text-slate-500">Última sincronização</p>
                <p className="text-slate-800">{status.last_tested ? new Date(status.last_tested).toLocaleString('pt-BR') : '—'}</p>
              </div>
              <div>
                <p className="text-slate-500">Última mensagem</p>
                <p className="text-slate-800">{status.last_message_at ? new Date(status.last_message_at).toLocaleString('pt-BR') : '—'}</p>
              </div>
            </div>

            {status.last_error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {status.last_error}
              </div>
            )}

            {/* ─── Interruptor da IA ─── */}
            <div className="bg-gradient-to-r from-indigo-50 to-emerald-50 border border-indigo-200 rounded-lg p-4 flex items-center justify-between" data-testid="ai-toggle-panel">
              <div className="flex-1">
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  <span className={status.ai_enabled ? 'text-emerald-600' : 'text-slate-400'}>●</span>
                  IA de atendimento automático
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {status.ai_enabled
                    ? 'A IA responde automaticamente às mensagens dos seus clientes.'
                    : 'IA desligada. Você continua conectado, mas responde manualmente.'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!status.ai_enabled}
                disabled={aiToggling}
                onClick={toggleAI}
                data-testid="ai-toggle-switch"
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 ${
                  status.ai_enabled ? 'bg-emerald-500' : 'bg-slate-300'
                } ${aiToggling ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    status.ai_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              <Button onClick={testConnection} disabled={testing} variant="outline" data-testid="test-connection-btn">
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Testar conexão
              </Button>
              <Button onClick={() => setShowRegister(true)} variant="outline" data-testid="reconnect-btn">
                <Send className="w-4 h-4 mr-2" /> Reconectar
              </Button>
              <Button onClick={disconnect} disabled={disconnecting} variant="destructive" data-testid="disconnect-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Desconectar
              </Button>
            </div>

            {status.status === 'connected' && (
              <div className="pt-4 border-t border-slate-100">
                <p className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Send className="w-4 h-4" /> Enviar mensagem de teste
                </p>
                <div className="flex gap-2">
                  <Input
                    placeholder="+5511987654321"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    data-testid="test-phone-input"
                  />
                  <Button onClick={sendTest} disabled={sendingTest} data-testid="send-test-btn">
                    {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Formato E.164 (com +55). O número precisa ter iniciado uma conversa nas últimas 24h ou ser destinatário aprovado no Meta Business Manager.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="whatsapp-not-configured">
            <Smartphone className="w-14 h-14 mx-auto text-slate-400 mb-3" />
            <h2 className="text-lg font-semibold text-slate-900">Nenhum WhatsApp conectado</h2>
            <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
              Conecte seu número oficial usando a WhatsApp Cloud API da Meta. Nenhum Twilio, nenhum intermediário.
            </p>
            <Button onClick={() => setShowRegister(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="register-whatsapp-btn">
              <Send className="w-4 h-4 mr-2" /> Registrar WhatsApp
            </Button>
          </div>
        )}

        {/* Modal de registro */}
        {showRegister && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowRegister(false)}>
            <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Registrar WhatsApp</h2>
                  <p className="text-xs text-slate-500 mt-1">Cole as credenciais do seu WhatsApp Business (Meta)</p>
                </div>
                <button onClick={() => setShowRegister(false)} className="text-slate-400 hover:text-slate-700" data-testid="close-register-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-xs text-indigo-900 mb-4 space-y-1">
                <p className="flex items-start gap-1"><Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Como obter as credenciais:</p>
                <ol className="list-decimal list-inside space-y-0.5 pl-4">
                  <li>Acesse <a href="https://business.facebook.com" target="_blank" rel="noreferrer" className="underline">business.facebook.com</a> → WhatsApp Manager</li>
                  <li>Crie um System User com permissão `whatsapp_business_management` e `whatsapp_business_messaging`</li>
                  <li>Gere um Access Token permanente (Never Expires) desse System User</li>
                  <li>Copie o Phone Number ID e o WABA ID da API Setup</li>
                </ol>
                <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline font-medium mt-1">
                  Documentação oficial <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <form onSubmit={submitRegister} className="space-y-3" data-testid="whatsapp-register-form">
                <div>
                  <label className="text-sm font-medium text-slate-700">Access Token *</label>
                  <Input
                    type="password"
                    required
                    placeholder="EAAK... (System User Token)"
                    value={form.access_token}
                    onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                    data-testid="access-token-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Phone Number ID *</label>
                  <Input
                    required
                    placeholder="1234567890"
                    value={form.phone_number_id}
                    onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
                    data-testid="phone-number-id-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">WhatsApp Business Account ID *</label>
                  <Input
                    required
                    placeholder="9876543210"
                    value={form.waba_id}
                    onChange={(e) => setForm({ ...form, waba_id: e.target.value })}
                    data-testid="waba-id-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Business Manager ID (opcional)</label>
                  <Input
                    placeholder="Opcional"
                    value={form.business_id}
                    onChange={(e) => setForm({ ...form, business_id: e.target.value })}
                    data-testid="business-id-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Número WhatsApp *</label>
                  <Input
                    required
                    placeholder="+5511987654321"
                    value={form.whatsapp_number}
                    onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                    data-testid="whatsapp-number-input"
                  />
                </div>

                <div className="pt-2 flex gap-2">
                  <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-register-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                    Salvar e testar
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowRegister(false)}>Cancelar</Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
